<script>
import CurrentVisitor from '@/components/statistics/CurrentVisitor'
import BumpTime from '@/components/statistics/BumpTime'
import UserOrigin from '@/components/statistics/UserOrigin'
import UserGender from '@/components/statistics/UserGender'
import AgeRange from '@/components/statistics/AgeRange'
import gql from 'graphql-tag'
import Button from '@/components/Button'
import DateCard from '@/components/DateCard'
import TotalCheckins from '@/components/statistics/TotalCheckins'
import DLoader from '@/components/DLoader'
import Modal from '@/components/Modal'
import DInput from '@/components/Input'

export default {
    name: 'VenueStatistic',
    components: {
        CurrentVisitor,
        BumpTime,
        UserOrigin,
        UserGender,
        AgeRange,
        Button,
        DateCard,
        TotalCheckins,
        DLoader,
        Modal,
        DInput,
    },
    data() {
        return {
            liveData: true,
            historicalData: false,
            startDate: new Date().setDate(new Date().getDate() - 7),
            endDate: new Date(),
            loading: false,
            sendPushNotificationsModal: false,
            notificationTitle: '',
            notificationBody: '',
            notificationReach: '',
        }
    },
    methods: {
        updateDate(dateTime) {
            this.startDate = dateTime.startDate
            this.endDate = dateTime.endDate
        },
        async sendPushNotifications() {
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($pageId: ID!, $notification: Notification!) {
                            sendPushToCheckedInUsers(pageId: $pageId, notification: $notification)
                        }
                    `,
                    variables: {
                        pageId: this.$route.params.id,
                        notification: {
                            title: this.notificationTitle,
                            body: this.notificationBody,
                        },
                    },
                })
                .then((data) => {
                    this.notificationReach = data.data.sendPushToCheckedInUsers
                })
        },
    },
    apollo: {
        liveVenueAnalytics: {
            query: gql`
                query ($id: ID!) {
                    liveVenueAnalytics(venueID: $id) {
                        visitors {
                            new
                            recurring
                            vip
                            timeDistribution {
                                occurence
                                timestamp
                            }
                            ageDistribution {
                                age
                                occurrence
                            }
                            genderDistribution {
                                diverse
                                female
                                male
                                unspecified
                            }
                            originDistribution {
                                city
                                occurrence
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id,
                }
            },
            pollInterval: 5000,
        },
        historicalVenueAnalytics: {
            query: gql`
                query ($id: ID!, $startDate: Date, $endDate: Date) {
                    historicalVenueAnalytics(venueID: $id, startDate: $startDate, endDate: $endDate) {
                        visitors {
                            new
                            recurring
                            vip
                            timeDistribution {
                                occurence
                                timestamp
                            }
                            ageDistribution {
                                age
                                occurrence
                            }
                            genderDistribution {
                                diverse
                                female
                                male
                                unspecified
                            }
                            originDistribution {
                                city
                                occurrence
                            }
                        }
                        totalCheckins
                        totalCheckouts
                        hourDistribution {
                            hour
                            occurence
                        }
                    }
                }
            `,
            variables() {
                if (this.startDate && this.endDate) {
                    return {
                        id: this.$route.params.id,
                        startDate: this.startDate,
                        endDate: this.endDate,
                    }
                } else {
                    return {
                        id: this.$route.params.id,
                    }
                }
            },
        },
    },
}
</script>

<template>
    <div class="venue-statistic">
        <div class="titleLoader">
            <h2 class="title-1">Statistics</h2>
            <DLoader v-show="historicalVenueAnalytics.loading" />
        </div>
        <Button
            v-if="historicalData"
            type="secondary"
            :value="'Live'"
            icon="disc"
            style="margin-bottom: 20px; margin-right: 20px"
            @click="historicalData = false"
        />
        <Button
            v-else-if="!historicalData"
            type="red"
            :value="'Live'"
            icon="disc"
            style="margin-bottom: 20px; margin-right: 20px"
            @click="historicalData = false"
        />
        <Button
            v-if="!historicalData"
            type="secondary"
            :value="'Historical Data'"
            icon="bar-chart"
            style="margin-bottom: 20px; margin-right: 20px"
            @click="historicalData = true"
        />
        <Button
            v-else-if="historicalData"
            type="primary"
            :value="'Historical Data'"
            icon="bar-chart"
            style="margin-bottom: 20px; margin-right: 20px"
            @click="historicalData = true"
        />
        <Button
            type="secondary"
            :value="'Send Notifications'"
            icon="mail"
            style="margin-bottom: 20px; margin-right: 20px"
            @click="sendPushNotificationsModal = true"
        />
        <div class="col0">
            <DateCard v-if="historicalData" :value="{ startDate: startDate, endDate: endDate }" @input="updateDate" />
            <TotalCheckins
                v-if="historicalData"
                :total-checkins="historicalVenueAnalytics.totalCheckins"
                :total-checkouts="historicalVenueAnalytics.totalCheckouts"
            />
        </div>
        <div class="col1">
            <CurrentVisitor
                :venue-analytics="historicalData ? historicalVenueAnalytics : liveVenueAnalytics"
                :show-count="true"
                :historical-data="historicalData"
            />
            <BumpTime
                :venue-analytics="historicalData ? historicalVenueAnalytics : liveVenueAnalytics"
                :historical-data="historicalData"
            />
        </div>
        <div class="col2">
            <UserOrigin
                :venue-analytics="historicalData ? historicalVenueAnalytics : liveVenueAnalytics"
                :historical-data="historicalData"
            />
            <UserGender
                :venue-analytics="historicalData ? historicalVenueAnalytics : liveVenueAnalytics"
                :historical-data="historicalData"
            />
            <AgeRange
                :venue-analytics="historicalData ? historicalVenueAnalytics : liveVenueAnalytics"
                :historical-data="historicalData"
            />
        </div>
        <Modal
            v-if="sendPushNotificationsModal"
            class="import-venue-modal"
            @cancel="sendPushNotificationsModal = false"
            @confirm="sendPushNotifications"
        >
            <div class="modal-content">
                <div class="headline-container">
                    <div class="copy-l bold">Send Push Notifications to Checked-in Users</div>
                </div>
                <p v-if="notificationReach !== ''">The notification was sent to {{ notificationReach }} devices.</p>
                <div v-if="notificationReach === ''" class="main">
                    <DInput v-model="notificationTitle" :placeholder="'Title'" style="margin-bottom: 1.2rem"></DInput>
                    <DInput v-model="notificationBody" :placeholder="'Message'" style="margin-bottom: 1.2rem"></DInput>
                </div>
                <div v-if="notificationReach === ''" class="action-container fullwidth">
                    <Button type="secondary" :value="'Cancel'" @click="sendPushNotificationsModal = false" />
                    <Button type="primary" :value="'Submit'" @click="sendPushNotifications" />
                </div>
            </div>
        </Modal>
    </div>
</template>

<style scoped lang="sass">
.venue-statistic
    .titleLoader
        display: flex
        align-items: baseline
        justify-content: space-between

    h2
        margin-bottom: $m

    .d-loader
        width: 20px
        height: 20px

    .col0
        justify-items: stretch
        display: grid
        grid-gap: $m
        margin-bottom: 12px
        box-sizing: border-box

        .date-card, .total-checkins
            width: 100%

        ::v-deep .calendar-container
            margin-bottom: 0


    .col1, .col2
        justify-items: stretch
        align-items: stretch
        display: grid
        grid-gap: $m
        margin-bottom: $m
    .col0
        grid-template-columns: repeat(2, 1fr)
    .col1
        grid-template-columns: 441px 428px
    .col2
        grid-template-columns: 248px 318px 286px

    @include breakpoint('l')
        .col0, .col1, .col2
            grid-template-columns: 1fr
</style>
